import {
  Component,
  Input,
  Output,
  EventEmitter,
  ViewEncapsulation,
} from '@angular/core';

// ── AppCardComponent (<app-card>, [app-card]) ──────────────────────────────
@Component({
  selector: 'app-card, [app-card]',
  template: `<ng-content></ng-content>`,
  styleUrls: ['./app-card.component.scss'],
  encapsulation: ViewEncapsulation.None,
  host: {
    class: 'app-card',
    '[class.app-card--row]': "layout === 'row'",
    '[class.app-card--column]': "layout === 'column'",
    '[class.app-card--stat]': "variant === 'stat'",
    '[class.app-card--flat]': "variant === 'flat'",
    '[class.app-card--outline]': "variant === 'outline'",
    '[class.app-card--hoverable]': 'hoverable || clickable',
    '[class.app-card--selected]': 'selected',
    '[style.padding]': 'computedPadding',
    '[style.width]': 'width || null',
    '[style.height]': 'height || null',
    '[style.borderRadius]': 'borderRadius || null',
    '[style.border]': 'border || null',
    '[style.backgroundColor]': 'background || null',
    '(click)': 'onClick($event)',
  },
})
export class AppCardComponent {
  @Input() variant: 'default' | 'stat' | 'flat' | 'outline' = 'default';
  @Input() layout?: 'row' | 'column';
  @Input() hoverable: boolean = false;
  @Input() selected: boolean = false;
  @Input() padding?: string;
  @Input() width?: string;
  @Input() height?: string;
  @Input() borderRadius?: string;
  @Input() border?: string;
  @Input() background?: string;
  @Input() clickable: boolean = false;
  @Input() customClass: string = '';

  @Output() cardClick = new EventEmitter<MouseEvent>();

  get computedPadding(): string | null {
    if (!this.padding) return null;
    switch (this.padding) {
      case 'none':
        return '0';
      case 'xs':
        return '8px 10px';
      case 'sm':
        return '10px 12px';
      case 'md':
        return '14px 16px';
      case 'lg':
        return '20px 24px';
      default:
        return this.padding;
    }
  }

  onClick(event: MouseEvent): void {
    if (this.hoverable || this.clickable) {
      this.cardClick.emit(event);
    }
  }
}

// ── AppCardAvatarComponent (<app-card-avatar>) ─────────────────────────────
@Component({
  selector: 'app-card-avatar',
  template: `
    <div
      class="app-card-avatar"
      [class.app-card-avatar--sm]="size === 'sm'"
      [class.app-card-avatar--md]="size === 'md'"
      [class.app-card-avatar--lg]="size === 'lg'"
      [class.app-card-avatar--xl]="size === 'xl'"
      [ngClass]="customClass"
      [style.backgroundColor]="bgColor || null"
    >
      <img *ngIf="src" [src]="src" [alt]="alt" class="app-card-avatar__img" />
      <span
        *ngIf="!src && initials"
        class="app-card-avatar__initial"
        [style.color]="textColor || null"
      >
        {{ initials }}
      </span>
      <ng-content *ngIf="!src && !initials"></ng-content>
    </div>
  `,
  encapsulation: ViewEncapsulation.None,
})
export class AppCardAvatarComponent {
  @Input() initials: string = '';
  @Input() src: string = '';
  @Input() alt: string = 'avatar';
  @Input() size: 'sm' | 'md' | 'lg' | 'xl' = 'md';
  @Input() bgColor: string = '';
  @Input() textColor: string = '';
  @Input() customClass: string = '';
}

// ── AppCardBadgeComponent (<app-card-badge>) ───────────────────────────────
@Component({
  selector: 'app-card-badge',
  template: `
    <span
      class="app-card-badge"
      [class.app-card-badge--blue]="variant === 'blue'"
      [class.app-card-badge--green]="variant === 'green'"
      [class.app-card-badge--purple]="variant === 'purple'"
      [class.app-card-badge--amber]="variant === 'amber'"
      [class.app-card-badge--red]="variant === 'red'"
      [class.app-card-badge--gray]="variant === 'gray'"
      [ngClass]="customClass"
    >
      <ng-content></ng-content>
    </span>
  `,
  encapsulation: ViewEncapsulation.None,
})
export class AppCardBadgeComponent {
  @Input() variant: 'blue' | 'green' | 'purple' | 'amber' | 'red' | 'gray' =
    'blue';
  @Input() customClass: string = '';
}

// ── AppCardFieldComponent (<app-card-field>) ───────────────────────────────
@Component({
  selector: 'app-card-field',
  template: `
    <div
      class="app-card-field"
      [class.app-card-field--left]="align === 'left'"
      [class.app-card-field--center]="align === 'center'"
      [class.app-card-field--right]="align === 'right'"
      [ngClass]="customClass"
      [style.flex]="flex ? (flex === 1 ? '1 1 0%' : flex) : null"
    >
      <span *ngIf="label" class="app-card-field__label">{{ label }}</span>
      <span
        *ngIf="value !== undefined && value !== null && value !== ''"
        class="app-card-field__value"
        >{{ value }}</span
      >
      <span *ngIf="subtext" class="app-card-field__subtext">{{ subtext }}</span>
      <ng-content></ng-content>
    </div>
  `,
  encapsulation: ViewEncapsulation.None,
})
export class AppCardFieldComponent {
  @Input() label: string = '';
  @Input() value: any = '';
  @Input() subtext: string = '';
  @Input() align: 'left' | 'center' | 'right' = 'left';
  @Input() flex: string | number = '';
  @Input() customClass: string = '';
}

// ── AppCardChipComponent (<app-card-chip>) ─────────────────────────────────
@Component({
  selector: 'app-card-chip',
  template: `
    <span class="app-card-chip" [ngClass]="customClass">
      <ng-content></ng-content>
    </span>
  `,
  encapsulation: ViewEncapsulation.None,
})
export class AppCardChipComponent {
  @Input() customClass: string = '';
}

// ── AppCardProgressComponent (<app-card-progress>) ─────────────────────────
@Component({
  selector: 'app-card-progress',
  template: `
    <div class="app-card-progress" [ngClass]="customClass">
      <div class="app-card-progress__bar-wrap">
        <div
          class="app-card-progress__bar-fill"
          [class.app-card-progress__bar-fill--exceeded]="isExceeded"
          [style.width.%]="safeValue"
        ></div>
      </div>
      <div
        class="app-card-progress__labels"
        *ngIf="spentAmount !== undefined || limitAmount !== undefined"
      >
        <div
          class="app-card-progress__stat app-card-progress__stat--left"
          *ngIf="spentAmount !== undefined"
        >
          <span class="app-card-progress__stat-value">{{ spentAmount }}</span>
          <span class="app-card-progress__stat-label">{{ spentLabel }}</span>
        </div>
        <div
          class="app-card-progress__stat app-card-progress__stat--right"
          *ngIf="limitAmount !== undefined"
        >
          <span class="app-card-progress__stat-value">{{ limitAmount }}</span>
          <span class="app-card-progress__stat-label">{{ limitLabel }}</span>
        </div>
      </div>
    </div>
  `,
  encapsulation: ViewEncapsulation.None,
})
export class AppCardProgressComponent {
  @Input() value: number = 0;
  @Input() spentAmount?: any;
  @Input() limitAmount?: any;
  @Input() spentLabel: string = 'Spent';
  @Input() limitLabel: string = 'Limit';
  @Input() isExceeded: boolean = false;
  @Input() customClass: string = '';

  get safeValue(): number {
    return Math.min(Math.max(this.value || 0, 0), 100);
  }
}

// ── AppCardStatComponent (<app-card-stat>) ─────────────────────────────────
@Component({
  selector: 'app-card-stat',
  template: `
    <app-card
      variant="stat"
      [customClass]="customClass"
      (cardClick)="statClick.emit($event)"
      [hoverable]="clickable"
    >
      <span class="app-card-stat__label">{{ label }}</span>
      <span class="app-card-stat__value">{{ value }}</span>
      <span *ngIf="subtext" class="app-card-stat__subtext">{{ subtext }}</span>
      <ng-content></ng-content>
    </app-card>
  `,
  encapsulation: ViewEncapsulation.None,
})
export class AppCardStatComponent {
  @Input() label: string = '';
  @Input() value: any = '';
  @Input() subtext: string = '';
  @Input() customClass: string = '';
  @Input() clickable: boolean = false;

  @Output() statClick = new EventEmitter<MouseEvent>();
}
